import { useState, useCallback, useMemo } from "react";
import {
    ClipboardList,
    CalendarX,
    UserX,
    ArrowRightLeft,
    Play,
} from "lucide-react";
import AsyncSelect from "react-select/async";
import { keepPreviousData } from "@tanstack/react-query";
import { callAdminAPI } from "../../api/callAdmin.js";
import RowsPerPageSelect from "../../components/RowsPerPageSelect.jsx";
import {
    useCallLogs,
    useQueue,
    useQueueTypeCounts,
    useCrmUsers,
    useOverviewStats,
    useAgentQueue,
    useAgentLogs,
    useAgentOrders,
    useAddToQueue,
    useRedistribute,
    useRunPopulate,
} from "../../hooks/useCallAdmin.js";

const CALL_TYPES = [
    { value: "scheduled", label: "Scheduled" },
    { value: "carryover", label: "Carryover" },
    { value: "follow_up", label: "Follow Up" },
    { value: "manual", label: "Manual" },
];
const CALL_TYPE_LABELS = Object.fromEntries(
    CALL_TYPES.map((t) => [t.value, t.label]),
);

const OUTCOME_LABEL = {
    switched_off: "Switched Off",
    network_issue: "Network Issue",
    reject_call: "Rejected Call",
    no_answer: "No Answer",
    prev_order: "Prev Order",
    ready_to_order: "Ready to Order",
    callback: "Callback",
    not_interest: "Not Interested",
    new_number: "New Number",
    closed: "Closed",
    vendor_change: "Vendor Change",
    other: "Other",
};

const STATUS_COLORS = {
    connected: { bg: "#dcfce7", color: "#166534" },
    not_answer: { bg: "#fef9c3", color: "#854d0e" },
    not_connected: { bg: "#fee2e2", color: "#991b1b" },
};
const QUEUE_STATUS_COLORS = {
    ordered: { bg: "#dcfce7", color: "#166534" },
    called: { bg: "#dbeafe", color: "#1e40af" },
    pending: { bg: "#fef9c3", color: "#854d0e" },
    skipped: { bg: "#f3f4f6", color: "#6b7280" },
};

const FLAG_CHIPS = [
    { val: "green ++", label: "Green++", bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    { val: "green", label: "Green", bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
    { val: "yellow", label: "Yellow", bg: "#fefce8", color: "#ca8a04", border: "#fde047" },
    { val: "red", label: "Red", bg: "#fef2f2", color: "#dc2626", border: "#fca5a5" },
];

// ─── AsyncSelect styles ───────────────────────────────────────────────────────
const selectStyles = {
    control: (b, s) => ({
        ...b,
        minHeight: 34,
        fontSize: "0.84rem",
        borderRadius: 7,
        borderColor: s.isFocused ? "#3b82f6" : "#d1d5db",
        boxShadow: s.isFocused ? "0 0 0 1px #3b82f6" : "none",
        "&:hover": { borderColor: "#3b82f6" },
    }),
    valueContainer: (b) => ({ ...b, padding: "2px 8px" }),
    singleValue: (b) => ({ ...b, fontSize: "0.84rem", color: "#111827" }),
    placeholder: (b) => ({ ...b, fontSize: "0.84rem", color: "#9ca3af" }),
    option: (b, s) => ({
        ...b,
        fontSize: "0.84rem",
        background: s.isSelected ? "#eff6ff" : s.isFocused ? "#f8fafc" : "#fff",
        color: s.isSelected ? "#1d4ed8" : "#111827",
    }),
    menu: (b) => ({
        ...b,
        borderRadius: 9,
        boxShadow: "0 8px 24px rgba(0,0,0,0.13)",
        zIndex: 200,
    }),
    dropdownIndicator: (b) => ({ ...b, padding: "0 6px" }),
    clearIndicator: (b) => ({ ...b, padding: "0 6px" }),
    indicatorSeparator: () => ({ display: "none" }),
};

// ─── SearchSelect ─────────────────────────────────────────────────────────────
function SearchSelect({ placeholder, value, onSelect, fetchOptions, disabled = false }) {
    const toOpt = (item) =>
        item ? { value: item.id, label: item.primaryText, sub: item.secondaryText } : null;
    const fromOpt = (opt) =>
        opt ? { id: opt.value, primaryText: opt.label, secondaryText: opt.sub } : null;

    const loadOptions = useCallback(
        async (input) => {
            try {
                const items = await fetchOptions(input);
                return items.map(toOpt);
            } catch {
                return [];
            }
        },
        [fetchOptions],
    );

    return (
        <AsyncSelect
            placeholder={placeholder}
            value={toOpt(value)}
            onChange={(opt) => onSelect(fromOpt(opt))}
            loadOptions={loadOptions}
            defaultOptions
            cacheOptions
            isClearable
            isDisabled={disabled}
            styles={selectStyles}
            formatOptionLabel={(opt) => (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 500 }}>{opt.label}</span>
                    {opt.sub && (
                        <span style={{ fontSize: "0.78rem", color: "#9ca3af", flexShrink: 0 }}>
                            {opt.sub}
                        </span>
                    )}
                </div>
            )}
            noOptionsMessage={({ inputValue }) => inputValue ? "No results." : "Start typing…"}
            loadingMessage={() => "Loading…"}
        />
    );
}

// ─── FilterLabel ──────────────────────────────────────────────────────────────
const FilterLabel = ({ children }) => (
    <span className="ca-filter-label">{children}</span>
);

// ─── Badge ────────────────────────────────────────────────────────────────────
const Badge = ({ bg, color, children }) => (
    <span className="ca-badge" style={{ background: bg, color }}>{children}</span>
);

// ─── Pagination ───────────────────────────────────────────────────────────────
const PAGE_LIMITS = [100, 150];

function Pagination({ page, pages, limit, onLimitChange, onChange }) {
    return (
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", marginTop: "1rem", flexWrap: "wrap" }}>
            <button
                onClick={() => onChange(page - 1)}
                disabled={page <= 1}
                className="ca-input"
                style={{ cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1, padding: "5px 14px" }}
            >
                ←
            </button>
            {pages > 0 && (
                <span style={{ fontSize: "0.83rem", color: "#6b7280", padding: "0 4px" }}>
                    Page {page} of {pages}
                </span>
            )}
            <button
                onClick={() => onChange(page + 1)}
                disabled={page >= pages}
                className="ca-input"
                style={{ cursor: page >= pages ? "not-allowed" : "pointer", opacity: page >= pages ? 0.4 : 1, padding: "5px 14px" }}
            >
                →
            </button>
            <RowsPerPageSelect
                value={limit}
                options={PAGE_LIMITS}
                onChange={(n) => { onLimitChange(n); onChange(1); }}
                style={{ marginLeft: 8 }}
            />
        </div>
    );
}

// ─── AddQueueModal ────────────────────────────────────────────────────────────
function AddQueueModal({ onClose, onSuccess, fetchCrmUsers }) {
    const [crmUser, setCrmUser] = useState(null);
    const [lead, setLead] = useState(null);
    const [leadFlag, setLeadFlag] = useState("");
    const [callType, setCallType] = useState("manual");
    const [error, setError] = useState("");

    const addMutation = useAddToQueue();

    const fetchLeadsFiltered = useCallback(
        async (query) => {
            const params = { limit: 20 };
            if (query) params.search = query;
            if (leadFlag) params.status_flag = leadFlag;
            const r = await callAdminAPI.searchLeads(params);
            return (r.data?.data || []).map((l) => ({
                id: l.id,
                primaryText: l.name,
                secondaryText: l.phone,
            }));
        },
        [leadFlag],
    );

    const handleFlagClick = (val) => {
        setLeadFlag((f) => (f === val ? "" : val));
        setLead(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!crmUser || !lead) {
            setError("Please select a CRM user and a lead.");
            return;
        }
        setError("");
        try {
            const r = await addMutation.mutateAsync({
                crm_user_id: crmUser.id,
                lead_id: lead.id,
                call_type: callType,
            });
            onSuccess(r.data.created ? "Lead added to queue." : "Already in queue — updated.");
        } catch (err) {
            setError(err.data?.error || "Failed to add. Try again.");
        }
    };

    return (
        <div className="ca-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="ca-modal">
                <div className="ca-modal-header">
                    <div>
                        <div className="ca-modal-title">Add to Call Queue</div>
                        <div className="ca-modal-subtitle">Assign a lead to a CRM user for today</div>
                    </div>
                    <button onClick={onClose} className="ca-modal-close">✕</button>
                </div>

                <form onSubmit={handleSubmit} className="ca-modal-body">
                    <div>
                        <FilterLabel>CRM User *</FilterLabel>
                        <SearchSelect
                            placeholder="Search CRM user…"
                            value={crmUser}
                            onSelect={setCrmUser}
                            fetchOptions={fetchCrmUsers}
                        />
                    </div>

                    <div>
                        <FilterLabel>Lead *</FilterLabel>
                        <div className="ca-flag-chips">
                            {FLAG_CHIPS.map((c) => (
                                <button
                                    key={c.val}
                                    type="button"
                                    onClick={() => handleFlagClick(c.val)}
                                    className="ca-flag-chip"
                                    style={leadFlag === c.val ? {
                                        borderColor: c.border,
                                        background: c.bg,
                                        color: c.color,
                                    } : {}}
                                >
                                    {c.label}
                                </button>
                            ))}
                            {leadFlag && (
                                <button
                                    type="button"
                                    onClick={() => { setLeadFlag(""); setLead(null); }}
                                    className="ca-flag-chip"
                                    style={{ background: "#fff", color: "#6b7280" }}
                                >
                                    ✕ Clear
                                </button>
                            )}
                        </div>
                        <SearchSelect
                            key={leadFlag}
                            placeholder="Search lead by name or phone…"
                            value={lead}
                            onSelect={setLead}
                            fetchOptions={fetchLeadsFiltered}
                        />
                    </div>

                    <div>
                        <FilterLabel>Call Type</FilterLabel>
                        <div className="ca-call-types">
                            {CALL_TYPES.map((t) => (
                                <label
                                    key={t.value}
                                    className={`ca-call-type-label${callType === t.value ? " active" : ""}`}
                                >
                                    <input
                                        type="radio"
                                        name="call_type"
                                        value={t.value}
                                        checked={callType === t.value}
                                        onChange={() => setCallType(t.value)}
                                        style={{ display: "none" }}
                                    />
                                    {t.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    {error && <div className="ca-error-box">{error}</div>}

                    <div className="ca-modal-actions">
                        <button type="button" onClick={onClose} className="ca-modal-btn-cancel">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={addMutation.isPending || !crmUser || !lead}
                            className="ca-modal-btn-primary"
                        >
                            {addMutation.isPending ? "Adding…" : "＋ Add to Queue"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ─── RedistributeModal ────────────────────────────────────────────────────────
function RedistributeModal({ user, date, onClose, onSuccess }) {
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const redistributeMutation = useRedistribute();

    const handleConfirm = async () => {
        setError("");
        try {
            const r = await redistributeMutation.mutateAsync({ absent_user_id: user.user_id, date });
            setResult(r.data);
        } catch (err) {
            setError(err.data?.error || "Redistribution failed.");
        }
    };

    const handleDone = () => {
        onSuccess(`${result.redistributed} leads redistributed from ${result.absentUser}.`);
    };

    return (
        <div
            className="ca-modal-overlay"
            onMouseDown={(e) => e.target === e.currentTarget && !result && onClose()}
        >
            <div className="ca-modal">
                <div className="ca-modal-header">
                    <div className="ca-modal-header-left">
                        <div className="ca-modal-icon">
                            <UserX size={18} color="#ef4444" />
                        </div>
                        <div>
                            <div className="ca-modal-title">Mark Absent &amp; Redistribute</div>
                            <div className="ca-modal-subtitle">{user.name} · {user.pending} pending leads</div>
                        </div>
                    </div>
                    {!result && (
                        <button onClick={onClose} className="ca-modal-close">✕</button>
                    )}
                </div>

                <div className="ca-modal-body ca-modal-body-lg">
                    {!result ? (
                        <>
                            <div className="ca-warn-box">
                                <strong>{user.pending}</strong> pending leads from{" "}
                                <strong>{user.name}</strong> will be distributed equally across the
                                other CRM users — those with fewer leads today get priority.
                            </div>

                            {error && <div className="ca-error-box">{error}</div>}

                            <div className="ca-modal-actions">
                                <button onClick={onClose} className="ca-modal-btn-cancel">Cancel</button>
                                <button
                                    onClick={handleConfirm}
                                    disabled={redistributeMutation.isPending || user.pending === 0}
                                    className="ca-modal-btn-danger"
                                >
                                    <ArrowRightLeft size={15} />
                                    {redistributeMutation.isPending ? "Redistributing…" : "Confirm Redistribution"}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="ca-result-box">
                                ✓ <strong>{result.redistributed}</strong> leads redistributed from{" "}
                                <strong>{result.absentUser}</strong>.
                            </div>

                            {result.distribution?.length > 0 && (
                                <div>
                                    <div className="ca-dist-heading">Distributed to</div>
                                    <div className="ca-dist-list">
                                        {result.distribution.map((d) => (
                                            <div key={d.user_id} className="ca-dist-row">
                                                <span className="ca-dist-name">{d.name}</span>
                                                <span className="ca-dist-count">+{d.count} leads</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <button onClick={handleDone} className="ca-modal-btn-done">Done</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── AbsentModal ─────────────────────────────────────────────────────────────
function AbsentModal({ onClose, fetchCrmUsers, queueSummary, onConfirm }) {
    const [selectedUser, setSelectedUser] = useState(null);

    const handleConfirm = () => {
        if (!selectedUser) return;
        const summary = queueSummary.find((s) => s.user_id === selectedUser.id);
        onConfirm({
            user_id: selectedUser.id,
            name: selectedUser.primaryText,
            pending: summary?.pending ?? 0,
        });
        onClose();
    };

    return (
        <div className="ca-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="ca-modal ca-modal-sm">
                <div className="ca-modal-header">
                    <div className="ca-modal-header-left">
                        <div className="ca-modal-icon">
                            <UserX size={18} color="#ef4444" />
                        </div>
                        <div>
                            <div className="ca-modal-title">Distribute Absent CRM Leads</div>
                            <div className="ca-modal-subtitle">Redistribute their pending queue to others</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="ca-modal-close">✕</button>
                </div>

                <div className="ca-modal-body">
                    <div>
                        <FilterLabel>Select Absent Agent *</FilterLabel>
                        <SearchSelect
                            placeholder="Search CRM agent…"
                            value={selectedUser}
                            onSelect={setSelectedUser}
                            fetchOptions={fetchCrmUsers}
                        />
                    </div>

                    {selectedUser && (() => {
                        const summary = queueSummary.find((s) => s.user_id === selectedUser.id);
                        const pending = summary?.pending ?? 0;
                        return (
                            <div className="ca-warn-box">
                                <strong>{pending}</strong> pending lead{pending !== 1 ? "s" : ""} from{" "}
                                <strong>{selectedUser.primaryText}</strong> will be distributed equally
                                across the other CRM users — those with fewer leads today get priority.
                                {pending === 0 && (
                                    <div style={{ marginTop: 4, fontSize: "0.78rem", color: "#92400e" }}>
                                        No queue data yet for today — pending count may update after refreshing.
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    <div className="ca-modal-actions">
                        <button onClick={onClose} className="ca-modal-btn-cancel">Cancel</button>
                        <button
                            onClick={handleConfirm}
                            disabled={!selectedUser}
                            className="ca-modal-btn-danger"
                        >
                            <ArrowRightLeft size={15} />
                            Continue to Redistribute
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── AgentDetailView ──────────────────────────────────────────────────────────
function AgentDetailView({ agent, onBack }) {
    const _today = new Date().toISOString().split("T")[0];
    const _firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        .toISOString().split("T")[0];

    // UI state only
    const [detailTab, setDetailTab] = useState("assigned");
    const [detailDateFrom, setDetailDateFrom] = useState(_firstOfMonth);
    const [detailDateTo, setDetailDateTo] = useState(_today);
    const [assignedPage, setAssignedPage] = useState(1);
    const [assignedLimit, setAssignedLimit] = useState(100);
    const [callLogsPage, setCallLogsPage] = useState(1);
    const [callLogsLimit, setCallLogsLimit] = useState(50);
    const [ordersPage, setOrdersPage] = useState(1);
    const [ordersLimit, setOrdersLimit] = useState(50);

    // ── Assigned queue query ──────────────────────────────────────────────────
    const { data: assignedData, isFetching: assignedLoading } = useAgentQueue(
        { user_id: agent.user_id, date: _today, page: assignedPage, limit: assignedLimit },
        { enabled: detailTab === "assigned" },
    );
    const assignedQueue = assignedData?.data || [];
    const assignedCount = assignedData?.count || 0;
    const assignedPages = assignedData?.totalPages || 1;

    // ── Call logs query ───────────────────────────────────────────────────────
    const callLogsParams = useMemo(() => {
        const p = { user_id: agent.user_id, page: callLogsPage, limit: callLogsLimit };
        if (detailDateFrom) p.date_from = detailDateFrom;
        if (detailDateTo) p.date_to = detailDateTo;
        return p;
    }, [agent.user_id, callLogsPage, callLogsLimit, detailDateFrom, detailDateTo]);

    const { data: callLogsData, isFetching: callLogsLoading } = useAgentLogs(
        callLogsParams,
        { enabled: detailTab === "calls" },
    );
    const callLogs = callLogsData?.data || [];
    const callLogsCount = callLogsData?.count || 0;
    const callLogsPages = callLogsData?.totalPages || 1;

    // ── Orders query ──────────────────────────────────────────────────────────
    const agentOrdersParams = useMemo(() => {
        const p = { placed_by: agent.user_id, page: ordersPage, limit: ordersLimit };
        if (detailDateFrom) p.date_from = detailDateFrom;
        if (detailDateTo) p.date_to = detailDateTo;
        return p;
    }, [agent.user_id, ordersPage, ordersLimit, detailDateFrom, detailDateTo]);

    const { data: agentOrdersData, isFetching: ordersLoading } = useAgentOrders(
        agentOrdersParams,
        { enabled: detailTab === "orders" },
    );
    const agentOrders = agentOrdersData?.data || [];
    const ordersCount = agentOrdersData?.count || 0;
    const ordersPages = agentOrdersData?.totalPages || 1;

    const initials = agent.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

    return (
        <div>
            {/* Back + header */}
            <div className="ca-detail-header">
                <button onClick={onBack} className="ca-btn-back">← Back</button>
                <div className="ca-detail-identity">
                    <div className="ca-agent-avatar">{initials}</div>
                    <div>
                        <div className="ca-detail-name">{agent.name}</div>
                        <div className="ca-detail-subtitle">Agent Activity Log</div>
                    </div>
                </div>
            </div>

            {/* Filter bar — only for calls / orders tabs */}
            {detailTab !== "assigned" && (
                <div className="ca-filter-bar">
                    <div className="ca-filter-row">
                        <div className="ca-filter-group">
                            <FilterLabel>From</FilterLabel>
                            <input
                                type="date"
                                className="ca-input"
                                value={detailDateFrom}
                                onChange={(e) => { setDetailDateFrom(e.target.value); setCallLogsPage(1); setOrdersPage(1); }}
                            />
                        </div>
                        <div className="ca-filter-group">
                            <FilterLabel>To</FilterLabel>
                            <input
                                type="date"
                                className="ca-input"
                                value={detailDateTo}
                                onChange={(e) => { setDetailDateTo(e.target.value); setCallLogsPage(1); setOrdersPage(1); }}
                            />
                        </div>
                        {(detailDateFrom || detailDateTo) && (
                            <button
                                onClick={() => { setDetailDateFrom(""); setDetailDateTo(""); }}
                                className="ca-btn-clear"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Sub-tabs */}
            <div className="ca-tabs">
                {[
                    { key: "assigned", label: "Assigned Customers", count: assignedCount },
                    { key: "calls", label: "Call Logs", count: callLogsCount },
                    { key: "orders", label: "Orders", count: ordersCount },
                ].map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setDetailTab(t.key)}
                        className={`ca-tab${detailTab === t.key ? " active" : ""}`}
                    >
                        {t.label}
                        {t.count > 0 && <span className="ca-tab-badge">{t.count}</span>}
                    </button>
                ))}
            </div>

            {/* Assigned customers table */}
            {detailTab === "assigned" && (
                <div>
                    <div className="ca-table-wrap">
                        <div className="ca-table-scroll">
                            <table className="ca-table">
                                <thead>
                                    <tr>
                                        {["#", "Customer", "Phone", "Call Type", "Flag", "Calls Made", "Status", "Scheduled"].map(
                                            (h) => <th key={h} className="ca-th">{h}</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignedLoading ? (
                                        <tr><td colSpan={8} className="ca-empty-cell">Loading…</td></tr>
                                    ) : assignedQueue.length === 0 ? (
                                        <tr><td colSpan={8} className="ca-empty-cell">No customers assigned for this date.</td></tr>
                                    ) : (
                                        assignedQueue.map((row, i) => {
                                            const qs = QUEUE_STATUS_COLORS[row.status] || QUEUE_STATUS_COLORS.skipped;
                                            return (
                                                <tr key={row.id}>
                                                    <td className="ca-td ca-td-muted" style={{ width: 40 }}>
                                                        {(assignedPage - 1) * assignedLimit + i + 1}
                                                    </td>
                                                    <td className="ca-td ca-td-bold">{row.lead?.name ?? "—"}</td>
                                                    <td className="ca-td ca-td-muted-nowrap">{row.lead?.phone ?? "—"}</td>
                                                    <td className="ca-td">
                                                        <Badge bg="#dbeafe" color="#1e40af">
                                                            {CALL_TYPE_LABELS[row.call_type] ?? row.call_type}
                                                        </Badge>
                                                    </td>
                                                    <td className="ca-td">{row.status_flag || "—"}</td>
                                                    <td className="ca-td ca-td-center ca-td-bold">{row.calls_made_today ?? 0}</td>
                                                    <td className="ca-td">
                                                        <Badge bg={qs.bg} color={qs.color}>{row.status}</Badge>
                                                    </td>
                                                    <td className="ca-td ca-td-small">
                                                        {row.next_scheduled_at
                                                            ? new Date(row.next_scheduled_at).toLocaleDateString("en-IN")
                                                            : "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <Pagination page={assignedPage} pages={assignedPages} limit={assignedLimit} onLimitChange={setAssignedLimit} onChange={setAssignedPage} />
                </div>
            )}

            {/* Call logs table */}
            {detailTab === "calls" && (
                <div>
                    <div className="ca-table-wrap">
                        <div className="ca-table-scroll">
                            <table className="ca-table">
                                <thead>
                                    <tr>
                                        {["#", "Lead", "Phone", "Status", "Outcome", "Client Said", "Agent Note", "Callback", "Date"].map(
                                            (h) => <th key={h} className="ca-th">{h}</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {callLogsLoading ? (
                                        <tr><td colSpan={9} className="ca-empty-cell">Loading…</td></tr>
                                    ) : callLogs.length === 0 ? (
                                        <tr><td colSpan={9} className="ca-empty-cell">No call logs found.</td></tr>
                                    ) : (
                                        callLogs.map((log, i) => {
                                            const sc = STATUS_COLORS[log.call_status] || { bg: "#f3f4f6", color: "#374151" };
                                            return (
                                                <tr key={log.id}>
                                                    <td className="ca-td ca-td-muted" style={{ width: 40 }}>
                                                        {(callLogsPage - 1) * callLogsLimit + i + 1}
                                                    </td>
                                                    <td className="ca-td ca-td-bold">{log.lead?.name ?? "—"}</td>
                                                    <td className="ca-td ca-td-muted-nowrap">{log.lead?.phone ?? "—"}</td>
                                                    <td className="ca-td"><Badge bg={sc.bg} color={sc.color}>{log.call_status}</Badge></td>
                                                    <td className="ca-td">{OUTCOME_LABEL[log.outcome] ?? log.outcome}</td>
                                                    <td className="ca-td ca-td-truncate" title={log.client_said}>{log.client_said || "—"}</td>
                                                    <td className="ca-td ca-td-truncate" title={log.agent_note}>{log.agent_note || "—"}</td>
                                                    <td className="ca-td ca-td-nowrap">{log.callback_at || "—"}</td>
                                                    <td className="ca-td ca-td-small">
                                                        {log.createdAt ? new Date(log.createdAt).toLocaleDateString("en-IN") : "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <Pagination page={callLogsPage} pages={callLogsPages} limit={callLogsLimit} onLimitChange={setCallLogsLimit} onChange={setCallLogsPage} />
                </div>
            )}

            {/* Orders table */}
            {detailTab === "orders" && (
                <div>
                    <div className="ca-table-wrap">
                        <div className="ca-table-scroll">
                            <table className="ca-table">
                                <thead>
                                    <tr>
                                        {["#", "Customer", "Phone", "Status", "Amount", "Payment", "Date"].map(
                                            (h) => <th key={h} className="ca-th">{h}</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordersLoading ? (
                                        <tr><td colSpan={7} className="ca-empty-cell">Loading…</td></tr>
                                    ) : agentOrders.length === 0 ? (
                                        <tr><td colSpan={7} className="ca-empty-cell">No orders found.</td></tr>
                                    ) : (
                                        agentOrders.map((order, i) => {
                                            const s = order.status;
                                            const badgeStyle =
                                                s === "delivered" ? { bg: "#dcfce7", color: "#166534" }
                                                    : s === "cancelled" ? { bg: "#fee2e2", color: "#991b1b" }
                                                        : s === "hold" ? { bg: "#f3f4f6", color: "#6b7280" }
                                                            : { bg: "#fef9c3", color: "#854d0e" };
                                            const label =
                                                s === "delivered" ? "Completed"
                                                    : s === "cancelled" ? "Cancelled"
                                                        : s === "hold" ? "On Hold"
                                                            : s === "pending" ? "Pending"
                                                                : s ?? "—";
                                            return (
                                                <tr key={order.id}>
                                                    <td className="ca-td ca-td-muted" style={{ width: 40 }}>
                                                        {(ordersPage - 1) * ordersLimit + i + 1}
                                                    </td>
                                                    <td className="ca-td ca-td-bold">
                                                        {order.lead?.name ?? order.customer_name ?? "—"}
                                                    </td>
                                                    <td className="ca-td ca-td-muted-nowrap">
                                                        {order.lead?.phone ?? order.customer_phone ?? "—"}
                                                    </td>
                                                    <td className="ca-td">
                                                        <Badge bg={badgeStyle.bg} color={badgeStyle.color}>{label}</Badge>
                                                    </td>
                                                    <td className="ca-td ca-td-bold">
                                                        {order.total_amount != null
                                                            ? `₹${Number(order.total_amount).toLocaleString("en-IN")}`
                                                            : "—"}
                                                    </td>
                                                    <td className="ca-td ca-td-muted">{order.payment_method ?? "—"}</td>
                                                    <td className="ca-td ca-td-small">
                                                        {order.created_at || order.createdAt
                                                            ? new Date(order.created_at || order.createdAt).toLocaleDateString("en-IN")
                                                            : "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <Pagination page={ordersPage} pages={ordersPages} limit={ordersLimit} onLimitChange={setOrdersLimit} onChange={setOrdersPage} />
                </div>
            )}
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CallAdminPage() {
    const [tab, setTab] = useState("queue");

    // ── Log filter state (UI only) ────────────────────────────────────────────
    const [logPage, setLogPage] = useState(1);
    const [logLimit, setLogLimit] = useState(40);
    const [logSearch, setLogSearch] = useState("");
    const [logStatus, setLogStatus] = useState("");
    const [logOutcome, setLogOutcome] = useState("");
    const [logDateFrom, setLogDateFrom] = useState("");
    const [logDateTo, setLogDateTo] = useState("");
    const [logUser, setLogUser] = useState(null);
    const [logLead, setLogLead] = useState(null);

    // ── Queue filter state (UI only) ──────────────────────────────────────────
    const [queuePage, setQueuePage] = useState(1);
    const [queueLimit, setQueueLimit] = useState(100);
    const [queueDate, setQueueDate] = useState(() => new Date().toISOString().split("T")[0]);
    const [queueUser, setQueueUser] = useState(null);
    const [queueLead, setQueueLead] = useState(null);
    const [queueCallType, setQueueCallType] = useState("");

    // ── Overview state ────────────────────────────────────────────────────────
    const [overviewDate, setOverviewDate] = useState(() => new Date().toISOString().split("T")[0]);
    const [selectedAgent, setSelectedAgent] = useState(null);

    // ── Modal / UI state ──────────────────────────────────────────────────────
    const [modalOpen, setModalOpen] = useState(false);
    const [absentModalOpen, setAbsentModalOpen] = useState(false);
    const [redistributeTarget, setRedistributeTarget] = useState(null);
    const [successMsg, setSuccessMsg] = useState("");

    // ── CRM Users (cached by React Query, 5-min stale) ───────────────────────
    const { data: crmUsersRaw = [] } = useCrmUsers();

    const fetchCrmUsers = useCallback(async (query) => {
        if (!query) {
            return crmUsersRaw.slice(0, 20).map((u) => ({
                id: u.id, primaryText: u.name, secondaryText: u.role?.name ?? "",
            }));
        }
        const q = query.toLowerCase();
        return crmUsersRaw
            .filter((u) => u.name.toLowerCase().includes(q))
            .slice(0, 20)
            .map((u) => ({ id: u.id, primaryText: u.name, secondaryText: u.role?.name ?? "" }));
    }, [crmUsersRaw]);

    const fetchLeads = useCallback(async (query) => {
        const params = { limit: 20 };
        if (query) params.search = query;
        const r = await callAdminAPI.searchLeads(params);
        return (r.data?.data || []).map((l) => ({
            id: l.id, primaryText: l.name, secondaryText: l.phone,
        }));
    }, []);

    // ── Logs query ────────────────────────────────────────────────────────────
    const logsParams = useMemo(() => {
        const p = { page: logPage, limit: logLimit };
        if (logSearch) p.search = logSearch;
        if (logStatus) p.call_status = logStatus;
        if (logOutcome) p.outcome = logOutcome;
        if (logDateFrom) p.date_from = logDateFrom;
        if (logDateTo) p.date_to = logDateTo;
        if (logUser) p.user_id = logUser.id;
        if (logLead) p.lead_id = logLead.id;
        return p;
    }, [logPage, logLimit, logSearch, logStatus, logOutcome, logDateFrom, logDateTo, logUser, logLead]);

    const { data: logsData, isFetching: logsLoading } = useCallLogs(logsParams, {
        enabled: tab === "logs",
    });
    const logs = logsData?.data || [];
    const logCount = logsData?.count || 0;
    const logPages = logsData?.totalPages || 1;

    // ── Queue query ───────────────────────────────────────────────────────────
    const queueParams = useMemo(() => {
        const p = { page: queuePage, limit: queueLimit, date: queueDate };
        if (queueUser) p.user_id = queueUser.id;
        if (queueLead) p.lead_id = queueLead.id;
        if (queueCallType === "scheduled") p.call_type = "scheduled";
        return p;
    }, [queuePage, queueLimit, queueDate, queueUser, queueLead, queueCallType]);

    const { data: queueData, isFetching: queueLoading, refetch: refetchQueue } = useQueue(queueParams, {
        enabled: tab === "queue",
        placeholderData: keepPreviousData,
    });
    const queue = queueData?.data || [];
    const queueCount = queueData?.count || 0;
    const queuePages = queueData?.totalPages || 1;
    const queueSummary = queueData?.summary || [];

    // ── Queue type counts (Normal / Scheduled buttons) ────────────────────────
    const queueCountsBase = useMemo(() => {
        const p = { date: queueDate };
        if (queueUser) p.user_id = queueUser.id;
        if (queueLead) p.lead_id = queueLead.id;
        return p;
    }, [queueDate, queueUser, queueLead]);

    const {
        data: queueTypeCounts = { all: 0, scheduled: 0, normal: 0 },
        isFetching: queueCountsLoading,
    } = useQueueTypeCounts(queueCountsBase, { enabled: tab === "queue" });

    // ── Overview query ────────────────────────────────────────────────────────
    const {
        data: overviewStats = [],
        isFetching: overviewLoading,
        refetch: refetchOverview,
    } = useOverviewStats(overviewDate, { enabled: tab === "overview" });

    // ── Run Populate mutation ─────────────────────────────────────────────────
    const runPopulateMutation = useRunPopulate();

    const handleRunPopulate = async () => {
        try {
            const r = await runPopulateMutation.mutateAsync();
            const d = r.data?.data;
            setSuccessMsg(`Queue populated: ${d?.totalQueued ?? 0} entries added for ${d?.date ?? "today"}.`);
            setTimeout(() => setSuccessMsg(""), 6000);
        } catch {
            setSuccessMsg("Queue population failed. Check server logs.");
            setTimeout(() => setSuccessMsg(""), 5000);
        }
    };

    const handleModalSuccess = (msg) => {
        setModalOpen(false);
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(""), 4000);
    };

    return (
        <div className="page-body">
            {/* Header */}
            <div className="ca-page-header">
                <div>
                    <h2 className="ca-page-title">Call Admin</h2>
                    <p className="ca-page-subtitle">Monitor call logs and manage daily CRM call queues.</p>
                </div>
                <div className="ca-header-actions">
                    <button
                        onClick={handleRunPopulate}
                        disabled={runPopulateMutation.isPending}
                        title="Run the daily queue population job now (idempotent)"
                        className={`ca-btn-run${runPopulateMutation.isPending ? " loading" : ""}`}
                    >
                        <Play size={14} />
                        {runPopulateMutation.isPending ? "Running…" : "Run Queue Job"}
                    </button>
                    <button
                        onClick={() => { setModalOpen(true); setSuccessMsg(""); }}
                        className="ca-btn-add"
                    >
                        <span className="ca-btn-add-icon">＋</span> Add to Queue
                    </button>
                </div>
            </div>

            {successMsg && (
                <div className="ca-success-bar">
                    <span>✓</span> {successMsg}
                </div>
            )}

            {/* Tabs */}
            <div className="ca-tabs">
                {[
                    { key: "overview", label: "User Overview", count: overviewStats.length },
                    { key: "queue", label: "CRM Queue", count: queueCount },
                    { key: "logs", label: "Call Logs", count: logCount },
                ].map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`ca-tab${tab === t.key ? " active" : ""}`}
                    >
                        {t.label}
                        {t.count > 0 && <span className="ca-tab-badge">{t.count}</span>}
                    </button>
                ))}
            </div>

            {/* ── USER OVERVIEW ──────────────────────────────────────────────────────── */}
            {tab === "overview" && (
                <div>
                    {selectedAgent ? (
                        <AgentDetailView agent={selectedAgent} onBack={() => setSelectedAgent(null)} />
                    ) : (
                        <>
                            {/* Date filter */}
                            <div className="ca-filter-bar">
                                <div className="ca-filter-row">
                                    <div className="ca-filter-group">
                                        <FilterLabel>Date</FilterLabel>
                                        <input
                                            type="date"
                                            className="ca-input"
                                            value={overviewDate}
                                            onChange={(e) => setOverviewDate(e.target.value)}
                                        />
                                    </div>
                                    <button
                                        onClick={() => refetchOverview()}
                                        disabled={overviewLoading}
                                        className="ca-btn-refresh"
                                    >
                                        ↻ Refresh
                                    </button>
                                </div>
                            </div>

                            {/* Cards grid */}
                            {overviewLoading ? (
                                <div className="ca-loading">Loading…</div>
                            ) : overviewStats.length === 0 ? (
                                <div className="ca-loading">No agents found.</div>
                            ) : (
                                <div className="ca-agent-grid">
                                    {overviewStats.map((stat) => {
                                        const initials = stat.name
                                            .split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
                                        return (
                                            <div
                                                key={stat.user_id}
                                                className="ca-agent-card"
                                                onClick={() => setSelectedAgent(stat)}
                                            >
                                                <div className="ca-agent-header">
                                                    <div className="ca-agent-avatar">{initials}</div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div className="ca-agent-name">{stat.name}</div>
                                                        {stat.role && <div className="ca-agent-role">{stat.role}</div>}
                                                    </div>
                                                </div>

                                                <div className="ca-agent-stats">
                                                    <div className="ca-stat-cell">
                                                        <div className="ca-stat-value">{stat.total}</div>
                                                        <div className="ca-stat-label">Assigned</div>
                                                    </div>
                                                    <div className="ca-stat-cell blue">
                                                        <div className="ca-stat-value">{stat.callsDone}</div>
                                                        <div className="ca-stat-label">Calls Done</div>
                                                    </div>
                                                    <div className="ca-stat-cell green">
                                                        <div className="ca-stat-value">{stat.ordersPlaced}</div>
                                                        <div className="ca-stat-label">Orders</div>
                                                    </div>
                                                </div>

                                                <div className="ca-agent-footer">
                                                    <span className="ca-agent-pending">{stat.pending} pending</span>
                                                    <span className="ca-agent-link">View details →</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ── CALL LOGS ──────────────────────────────────────────────────────────── */}
            {tab === "logs" && (
                <div>
                    {/* Filters */}
                    <div className="ca-filter-bar">
                        <div className="ca-filter-row-top">
                            <div className="ca-filter-flex-md">
                                <FilterLabel>Search</FilterLabel>
                                <input
                                    className="ca-input ca-input-full"
                                    placeholder="Name or phone…"
                                    value={logSearch}
                                    onChange={(e) => { setLogSearch(e.target.value); setLogPage(1); }}
                                />
                            </div>

                            <div className="ca-filter-flex">
                                <FilterLabel>CRM User</FilterLabel>
                                <SearchSelect
                                    placeholder="Search agent…"
                                    value={logUser}
                                    onSelect={(u) => { setLogUser(u); setLogPage(1); }}
                                    fetchOptions={fetchCrmUsers}
                                />
                            </div>

                            <div className="ca-filter-flex">
                                <FilterLabel>Lead</FilterLabel>
                                <SearchSelect
                                    placeholder="Search lead…"
                                    value={logLead}
                                    onSelect={(l) => { setLogLead(l); setLogPage(1); }}
                                    fetchOptions={fetchLeads}
                                />
                            </div>

                            <div className="ca-filter-flex-sm">
                                <FilterLabel>Status</FilterLabel>
                                <select
                                    className="ca-input ca-input-full"
                                    value={logStatus}
                                    onChange={(e) => { setLogStatus(e.target.value); setLogPage(1); }}
                                >
                                    <option value="">All</option>
                                    <option value="not_connected">Not Connected</option>
                                    <option value="not_answer">Not Answer</option>
                                    <option value="connected">Connected</option>
                                </select>
                            </div>

                            <div className="ca-filter-flex-sm">
                                <FilterLabel>Outcome</FilterLabel>
                                <select
                                    className="ca-input ca-input-full"
                                    value={logOutcome}
                                    onChange={(e) => { setLogOutcome(e.target.value); setLogPage(1); }}
                                >
                                    <option value="">All</option>
                                    {Object.entries(OUTCOME_LABEL).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="ca-filter-flex-lg">
                                <FilterLabel>Date Range</FilterLabel>
                                <div className="ca-date-range">
                                    <input
                                        type="date"
                                        className="ca-input"
                                        style={{ flex: 1 }}
                                        value={logDateFrom}
                                        onChange={(e) => { setLogDateFrom(e.target.value); setLogPage(1); }}
                                    />
                                    <span className="ca-filter-sep">to</span>
                                    <input
                                        type="date"
                                        className="ca-input"
                                        style={{ flex: 1 }}
                                        value={logDateTo}
                                        onChange={(e) => { setLogDateTo(e.target.value); setLogPage(1); }}
                                    />
                                </div>
                            </div>

                            {(logSearch || logUser || logLead || logStatus || logOutcome || logDateFrom || logDateTo) && (
                                <div style={{ display: "flex", alignItems: "flex-end", flex: "0 0 auto" }}>
                                    <button
                                        onClick={() => {
                                            setLogSearch(""); setLogUser(null); setLogLead(null);
                                            setLogStatus(""); setLogOutcome(""); setLogDateFrom(""); setLogDateTo(""); setLogPage(1);
                                        }}
                                        className="ca-btn-clear"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="ca-table-wrap">
                        <div className="ca-table-scroll">
                            <table className="ca-table">
                                <thead>
                                    <tr>
                                        {["#", "Lead", "Phone", "Agent", "Status", "Outcome", "Client Said", "Agent Note", "Callback", "Date"].map(
                                            (h) => <th key={h} className="ca-th">{h}</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {logsLoading ? (
                                        <tr>
                                            <td colSpan={10} className="ca-empty-cell">Loading…</td>
                                        </tr>
                                    ) : logs.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} className="ca-empty-cell">
                                                <ClipboardList size={36} color="#d1d5db" className="ca-empty-icon" />
                                                <div className="ca-empty-text">No call logs found.</div>
                                                <div className="ca-empty-hint">Try adjusting your filters.</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        logs.map((log, i) => {
                                            const sc = STATUS_COLORS[log.call_status] || { bg: "#f3f4f6", color: "#374151" };
                                            return (
                                                <tr key={log.id}>
                                                    <td className="ca-td ca-td-muted" style={{ width: 40 }}>
                                                        {(logPage - 1) * 30 + i + 1}
                                                    </td>
                                                    <td className="ca-td ca-td-bold">{log.lead?.name ?? "—"}</td>
                                                    <td className="ca-td ca-td-muted-nowrap">{log.lead?.phone ?? "—"}</td>
                                                    <td className="ca-td">{log.user?.name ?? "—"}</td>
                                                    <td className="ca-td">
                                                        <Badge bg={sc.bg} color={sc.color}>{log.call_status}</Badge>
                                                    </td>
                                                    <td className="ca-td">{OUTCOME_LABEL[log.outcome] ?? log.outcome}</td>
                                                    <td className="ca-td ca-td-truncate" title={log.client_said}>{log.client_said || "—"}</td>
                                                    <td className="ca-td ca-td-truncate" title={log.agent_note}>{log.agent_note || "—"}</td>
                                                    <td className="ca-td ca-td-nowrap">{log.callback_at || "—"}</td>
                                                    <td className="ca-td ca-td-small">
                                                        {log.createdAt
                                                            ? new Date(log.createdAt).toLocaleDateString("en-IN")
                                                            : "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <Pagination page={logPage} pages={logPages} limit={logLimit} onLimitChange={setLogLimit} onChange={setLogPage} />
                </div>
            )}

            {/* ── CRM QUEUE ──────────────────────────────────────────────────────────── */}
            {tab === "queue" && (
                <div>
                    <div className="ca-filter-bar">
                        <div className="ca-filter-row">
                            <div className="ca-filter-group">
                                <FilterLabel>Date</FilterLabel>
                                <input
                                    type="date"
                                    className="ca-input"
                                    value={queueDate}
                                    onChange={(e) => { setQueueDate(e.target.value); setQueuePage(1); }}
                                />
                            </div>

                            <div className="ca-filter-flex">
                                <FilterLabel>CRM User</FilterLabel>
                                <SearchSelect
                                    placeholder="Filter by user…"
                                    value={queueUser}
                                    onSelect={(u) => { setQueueUser(u); setQueuePage(1); }}
                                    fetchOptions={fetchCrmUsers}
                                />
                            </div>

                            <div className="ca-filter-flex">
                                <FilterLabel>Customer</FilterLabel>
                                <SearchSelect
                                    placeholder="Search Customer..."
                                    value={queueLead}
                                    onSelect={(l) => { setQueueLead(l); setQueuePage(1); }}
                                    fetchOptions={fetchLeads}
                                />
                            </div>

                            <button onClick={() => refetchQueue()} className="ca-btn-refresh">
                                ↻ Refresh
                            </button>

                            <button onClick={() => setAbsentModalOpen(true)} className="ca-btn-absent">
                                <UserX size={14} /> Distribute Absent CRM Leads
                            </button>
                        </div>

                        {/* Call-type filter buttons */}
                        <div className="ca-calltype-filter">
                            {[
                                { key: "", label: "All", count: queueTypeCounts.all },
                                { key: "normal", label: "Normal", count: queueTypeCounts.normal },
                                { key: "scheduled", label: "Scheduled", count: queueTypeCounts.scheduled },
                            ].map(({ key, label, count }) => (
                                <button
                                    key={key}
                                    onClick={() => { setQueueCallType(key); setQueuePage(1); }}
                                    className={`ca-calltype-btn${queueCallType === key ? " active" : ""}`}
                                    disabled={queueCountsLoading}
                                >
                                    {label}
                                    <span className="ca-calltype-count">
                                        {queueCountsLoading ? "…" : count}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {(() => {
                        const displayQueue = queueCallType === "normal"
                            ? queue.filter((r) => r.call_type !== "scheduled")
                            : queue;
                        return (
                            <>
                                <div className="ca-table-wrap">
                                    <div className="ca-table-scroll">
                                        <table className="ca-table">
                                            <thead>
                                                <tr>
                                                    {["#", "CRM User", "Lead", "Phone", "Type", "Priority", "Flag", "Calls", "Status", "Scheduled"].map(
                                                        (h) => <th key={h} className="ca-th">{h}</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {queueLoading ? (
                                                    <tr>
                                                        <td colSpan={10} className="ca-empty-cell">Loading…</td>
                                                    </tr>
                                                ) : displayQueue.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={10} className="ca-empty-cell">
                                                            <CalendarX size={36} color="#d1d5db" className="ca-empty-icon" />
                                                            <div className="ca-empty-text">No queue entries for {queueDate}.</div>
                                                            <div className="ca-empty-hint">Queue is populated by cron at 6 AM daily.</div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    displayQueue.map((row, i) => {
                                                        const qs = QUEUE_STATUS_COLORS[row.status] || QUEUE_STATUS_COLORS.skipped;
                                                        const missedSchedule = row.call_type === "scheduled" && row.status === "pending";
                                                        return (
                                                            <tr
                                                                key={row.id}
                                                                className={missedSchedule ? "ca-tr-missed" : "ca-tr-normal"}
                                                            >
                                                                <td className="ca-td ca-td-muted" style={{ width: 40 }}>
                                                                    {(queuePage - 1) * 40 + i + 1}
                                                                </td>
                                                                <td className="ca-td ca-td-bold">{row.crmUser?.name ?? "—"}</td>
                                                                <td className="ca-td">{row.lead?.name ?? "—"}</td>
                                                                <td className="ca-td ca-td-muted-nowrap">{row.lead?.phone ?? "—"}</td>
                                                                <td className="ca-td">
                                                                    <Badge bg="#dbeafe" color="#1e40af">
                                                                        {CALL_TYPE_LABELS[row.call_type] ?? row.call_type}
                                                                    </Badge>
                                                                </td>
                                                                <td className="ca-td ca-td-center ca-td-muted">{row.priority}</td>
                                                                <td className="ca-td">{row.status_flag}</td>
                                                                <td className="ca-td ca-td-center ca-td-bold">{row.calls_made_today}</td>
                                                                <td className="ca-td">
                                                                    <Badge bg={qs.bg} color={qs.color}>{row.status}</Badge>
                                                                </td>
                                                                <td className="ca-td ca-td-small">
                                                                    {row.next_scheduled_at
                                                                        ? new Date(row.next_scheduled_at).toLocaleDateString("en-IN")
                                                                        : "—"}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <Pagination page={queuePage} pages={queuePages} limit={queueLimit} onLimitChange={setQueueLimit} onChange={setQueuePage} />
                            </>
                        );
                    })()}
                </div>
            )}

            {modalOpen && (
                <AddQueueModal
                    onClose={() => setModalOpen(false)}
                    onSuccess={handleModalSuccess}
                    fetchCrmUsers={fetchCrmUsers}
                />
            )}

            {absentModalOpen && (
                <AbsentModal
                    onClose={() => setAbsentModalOpen(false)}
                    fetchCrmUsers={fetchCrmUsers}
                    queueSummary={queueSummary}
                    onConfirm={setRedistributeTarget}
                />
            )}

            {redistributeTarget && (
                <RedistributeModal
                    user={redistributeTarget}
                    date={queueDate}
                    onClose={() => setRedistributeTarget(null)}
                    onSuccess={(msg) => {
                        setRedistributeTarget(null);
                        setSuccessMsg(msg);
                        setTimeout(() => setSuccessMsg(""), 5000);
                    }}
                />
            )}
        </div>
    );
}
